import {
  ActivityFilterKey,
  MediaType,
  useCurrentWorkspace,
} from '@gripp/shared-logic';
import {
  ActivityListBase,
  AddIcon,
  Button,
  SlideAction,
  tokens,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActivityFoldOneColumn } from '../../activity/components/activityFoldOneColumn';
import { ActivitiesProps } from '../../activity/components/activityList';
import { NewIssueModal, NewIssueModalProps } from '../../issues';
import { useWindowDimensions } from '../../shared/hooks/useWindowDimensions';
import { useAssetRouting } from '../assetRouteContext';
import { ViewAllSectionHeader } from './viewAllSectionHeader';

export const AssetActivityFold = (props: ActivitiesProps) => {
  const windowDimensions = useWindowDimensions();
  const maxHeight = windowDimensions.height && windowDimensions.height - 280;
  const { issueId } = useParams();
  const location = useLocation();
  const isOnAssetPage = location.pathname.startsWith('/assets/');

  if (issueId && isOnAssetPage) {
    return (
      <ActivityFoldOneColumn
        assetId={props.assetId}
        onIssueSelected={props.onIssueSelected}
        selectedIssue={issueId}
        showAssetName={false}
        maxHeight={maxHeight}
      />
    );
  }

  return <AssetActivityFoldCompact {...props} />;
};

const AssetActivityFoldCompact = ({
  assetId,
  onIssueSelected,
}: ActivitiesProps) => {
  const { t } = useTranslation();
  const { onSlideChange } = useSlideLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath } = useAssetRouting(assetId);
  const [dataLoaded, setDataLoaded] = useState('');
  const [newIssueModalProps, setNewIssueModalProps] = useState<
    NewIssueModalProps | undefined
  >(undefined);
  const [issueModalVisible, setIssueModalVisible] = useState(false);
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const onIssueComplete = useCallback(() => {
    setIssueModalVisible(false);
    setNewIssueModalProps(undefined);
  }, []);

  const handleDataLoaded = useCallback((data: any[]) => {
    if (data?.length > 0) {
      setDataLoaded(data[0].id);
    } else {
      setDataLoaded('');
    }
  }, []);
  const isDetailPage = location.pathname.includes('/detail');

  const handleViewAllIssues = useCallback(async () => {
    if (!isDetailPage) {
      await onSlideChange(SlideAction.NEXT);

      navigate(`${basePath}/detail`);
    }
  }, [navigate, basePath, onSlideChange, isDetailPage]);

  const renderEmptyState = () => {
    return (
      <>
        <Button
          style={styles.addFileEmpty}
          leftIcon={<AddIcon />}
          variant={'rectangular'}
          onPress={() => {
            // show Modal to create issue
            setNewIssueModalProps({
              assetId: assetId,
              workspaceId: workspaceId,
              isOpened: true,
              modalType: 'modal',
              onComplete: onIssueComplete,
            });
            setIssueModalVisible(true);
          }}
        >
          {t('asset.header.buttons.addSomething')}
        </Button>

        {newIssueModalProps && (
          <NewIssueModal
            {...newIssueModalProps}
            isOpened={issueModalVisible}
            onComplete={onIssueComplete}
            acceptedMediaTypes={[MediaType.Image, MediaType.Document]}
          />
        )}
      </>
    );
  };

  const handleIssueSelected = useCallback(
    (id: string) => {
      onIssueSelected?.(id);
    },
    [onIssueSelected]
  );

  return (
    <View style={styles.containerHeightCropCircle}>
      <ViewAllSectionHeader
        title={t('asset.header.recentActivity')}
        onPress={handleViewAllIssues}
        disabled={!dataLoaded}
      />
      <View style={{ marginBottom: 24 }}>
        <ActivityListBase
          onIssueSelected={handleIssueSelected}
          onDataLoaded={handleDataLoaded}
          assetId={assetId}
          excludeFilter={true}
          showTextEmpty={true}
          cardVariant="assetCompact"
          pageSize={3}
          showTopBar={false}
          filterKey={
            assetId
              ? ActivityFilterKey.AssetActivity
              : ActivityFilterKey.Activity
          }
          customEmptyState={renderEmptyState()}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  containerHeightCropCircle: {
    width: '100%',
    paddingHorizontal: 16,
    paddingTop: 10,
  },
  activityListCropCircle: {
    height: '100%',
    minHeight: 100,
    overflow: 'hidden',
    marginBottom: 0,
  },
  addFileEmpty: {
    flex: 1,
    height: 56,
    maxHeight: 56,
    backgroundColor: tokens.colors.slate[100],
    width: '100%',
  },
});
